import React, { useCallback, useEffect, useState } from 'react';
import { LayoutChangeEvent, StyleSheet, ViewStyle } from 'react-native';
import Animated from 'react-native-reanimated';

import { Box, Row } from '@/components/lib/components';
import { useIsDesktopWeb } from '@/feature/responsive-design/hooks/use-is-desktop-web';
import {
    DESKTOP_CONTENT_TOP_GAP,
    DESKTOP_PAGE_MARGIN,
    MAX_DESKTOP_WEB_WIDTH,
    showScrollIndicator,
} from '@/utils/constants-platform-specific';

import { CasinoCategoryList } from '../components/CasinoCategoryList';
import { CasinoDesktopSideRail } from '../components/CasinoDesktopSideRail';
import { CasinoLobbyContentWidthContext } from '../hooks/use-casino-lobby-content-width';
import { useCasinoLobbyPagination } from '../hooks/use-casino-lobby-pagination';
import { useCasinoLobbyScreen } from '../hooks/use-casino-lobby-screen';

export const CasinoLobbyScreen = () => {
    const { isMaintenanceEnabled, isLobby, category, selectedCategoryId, setSelectedCategoryId, listRef } =
        useCasinoLobbyScreen();
    const isDesktopWeb = useIsDesktopWeb();
    const [scrollAreaHeight, setScrollAreaHeight] = useState(0);
    const [contentWidth, setContentWidth] = useState(0);
    const handleScrollPagination = useCasinoLobbyPagination({ isLobby, category });

    const onScrollAreaLayout = useCallback((event: LayoutChangeEvent) => {
        setScrollAreaHeight(event.nativeEvent.layout.height);
    }, []);

    const onContentLayout = useCallback((event: LayoutChangeEvent) => {
        setContentWidth(event.nativeEvent.layout.width);
    }, []);

    useEffect(() => {
        if (isDesktopWeb) {
            listRef.current?.scrollTo({ y: 0, animated: false });
        }
    }, [category, isDesktopWeb, listRef]);

    if (isMaintenanceEnabled) {
        return null;
    }

    const categoryList = (
        <CasinoCategoryList
            listRef={listRef}
            isLobby={isLobby}
            category={category}
            selectedCategoryId={selectedCategoryId}
            setSelectedCategoryId={setSelectedCategoryId}
        />
    );

    if (!isDesktopWeb) {
        return categoryList;
    }

    return (
        <Box flex={1} backgroundColor="background.neutral.base">
            <Animated.ScrollView
                ref={listRef}
                onLayout={onScrollAreaLayout}
                onScroll={handleScrollPagination}
                scrollEventThrottle={16}
                style={styles.page}
                contentContainerStyle={styles.pageContent}
                showsVerticalScrollIndicator={showScrollIndicator}
            >
                <Box width="100%" alignItems="center" style={styles.pageGrid}>
                    <Row width="100%" maxWidth={MAX_DESKTOP_WEB_WIDTH}>
                        <CasinoDesktopSideRail
                            category={category}
                            isLobby={isLobby}
                            setSelectedCategoryId={setSelectedCategoryId}
                            maxHeight={scrollAreaHeight ? scrollAreaHeight - DESKTOP_CONTENT_TOP_GAP * 2 : undefined}
                        />
                        <Box flex={1} minWidth={0} onLayout={onContentLayout}>
                            <CasinoLobbyContentWidthContext.Provider value={contentWidth}>
                                {categoryList}
                            </CasinoLobbyContentWidthContext.Provider>
                        </Box>
                    </Row>
                </Box>
            </Animated.ScrollView>
        </Box>
    );
};

const styles = StyleSheet.create({
    page: {
        flex: 1,
        scrollbarGutter: 'stable',
    } as ViewStyle,
    pageContent: {
        flexGrow: 1,
    },
    pageGrid: {
        paddingHorizontal: DESKTOP_PAGE_MARGIN,
        paddingTop: DESKTOP_CONTENT_TOP_GAP,
    },
});
